/**
 * Claims the client reads off an access token. Only the timing pair is modelled: nothing else in
 * the payload is the browser's business.
 */
export interface JwtTimingClaims {
	/** Seconds since the epoch, stamped by the server when the token was minted. */
	iat?: number;
	/** Seconds since the epoch at which the token stops being accepted. */
	exp?: number;
}

/**
 * Reads the claim payload out of a JWT without verifying it.
 *
 * Deliberately unverified: the client uses these only for timing decisions (how much of a token's
 * life is left, how far off the session cap is), never for a trust decision. The server validates
 * the signature on every request, so a tampered payload can at worst make this browser refresh
 * earlier or later than it needed to. Returns null rather than throwing, so a malformed or absent
 * token degrades to "unknown" at the call site instead of breaking the caller.
 */
export function readJwtClaims(token: string | null | undefined): JwtTimingClaims | null {
	try {
		const payload = token?.split('.')[1];
		if (!payload) {
			return null;
		}
		// Base64url to base64: atob rejects the URL-safe alphabet JWTs are encoded with.
		const normalised = payload.replace(/-/g, '+').replace(/_/g, '/');
		return JSON.parse(atob(normalised));
	} catch {
		return null;
	}
}
